/** Grouping of trade opportunities into merk → model → uitvoering with live counters. */

export function navOf(row) {
  const nav = row?.nav || {};
  const v = row?.vehicle_snapshot || {};
  const makeKey = nav.make_key || (v.make || "onbekend").trim().toLowerCase();
  const modelKey = nav.model_key || (v.model || "onbekend").trim().toLowerCase();
  const model = nav.model || v.model || "Onbekend model";
  const generation = nav.generation || v.generation || null;
  const overlap =
    generation &&
    (generation.toLowerCase().includes(model.toLowerCase()) ||
      model.toLowerCase().includes(generation.toLowerCase()));
  return {
    make: nav.make || v.make || "Onbekend merk",
    make_key: makeKey,
    model: generation && !overlap ? `${model} / ${generation}` : model,
    model_key: modelKey,
    type: nav.type || "Overige uitvoeringen",
    type_key: nav.type_key || "overig",
    generation,
  };
}

export const marketStatusOf = (row) => row?.market?.status || "BESTAAND";

export function rowStats(items) {
  const scores = [];
  const contributions = [];
  const rois = [];
  const seen = [];
  let newCount = 0;
  let dropCount = 0;
  let goneCount = 0;
  let backCount = 0;

  items.forEach((row) => {
    const h = row.headline || {};
    if (h.deal_score !== null && h.deal_score !== undefined) scores.push(h.deal_score);
    if (h.contribution !== null && h.contribution !== undefined) contributions.push(h.contribution);
    if (h.roi_pct !== null && h.roi_pct !== undefined) rois.push(h.roi_pct);
    const lastSeen = row.market?.last_seen_at;
    if (lastSeen) seen.push(lastSeen);
    const status = marketStatusOf(row);
    if (status === "NIEUW") newCount += 1;
    if (status === "PRIJS_VERLAAGD") dropCount += 1;
    if (status === "VERDWENEN") goneCount += 1;
    if (status === "TERUG_ONLINE") backCount += 1;
  });

  const sum = (list) => list.reduce((a, b) => a + b, 0);
  const positive = contributions.filter((c) => c > 0);
  return {
    count: items.length,
    best_score: scores.length ? Math.max(...scores) : null,
    best_contribution: contributions.length ? Math.max(...contributions) : null,
    total_contribution: positive.length ? Math.round(sum(positive)) : 0,
    avg_contribution: contributions.length ? Math.round(sum(contributions) / contributions.length) : null,
    avg_roi: rois.length ? Math.round((sum(rois) / rois.length) * 10) / 10 : null,
    new_count: newCount,
    price_drop_count: dropCount,
    disappeared_count: goneCount,
    back_online_count: backCount,
    last_scan_at: seen.length ? seen.sort().slice(-1)[0] : null,
  };
}

const byScoreThenLabel = (a, b) =>
  (b.stats.best_score ?? -1) - (a.stats.best_score ?? -1) || a.label.localeCompare(b.label);

export function buildTree(rows) {
  const makes = new Map();
  rows.forEach((row) => {
    const nav = navOf(row);
    if (!makes.has(nav.make_key)) {
      makes.set(nav.make_key, { key: nav.make_key, label: nav.make, items: [], models: new Map() });
    }
    const make = makes.get(nav.make_key);
    make.items.push(row);
    if (!make.models.has(nav.model_key)) {
      make.models.set(nav.model_key, {
        key: nav.model_key,
        label: nav.model,
        items: [],
        types: new Map(),
      });
    }
    const model = make.models.get(nav.model_key);
    model.items.push(row);
    if (!model.types.has(nav.type_key)) {
      model.types.set(nav.type_key, { key: nav.type_key, label: nav.type, items: [] });
    }
    model.types.get(nav.type_key).items.push(row);
  });

  return [...makes.values()]
    .map((make) => ({
      key: make.key,
      label: make.label,
      stats: rowStats(make.items),
      models: [...make.models.values()]
        .map((model) => ({
          key: model.key,
          label: model.label,
          stats: rowStats(model.items),
          types: [...model.types.values()]
            .map((type) => ({
              key: type.key,
              label: type.label,
              stats: rowStats(type.items),
              items: type.items,
            }))
            .sort(byScoreThenLabel),
        }))
        .sort(byScoreThenLabel),
    }))
    .sort(byScoreThenLabel);
}

export function matchesSelection(row, selection = {}) {
  if (!selection.make_key) return true;
  const nav = navOf(row);
  if (nav.make_key !== selection.make_key) return false;
  if (selection.model_key && nav.model_key !== selection.model_key) return false;
  if (selection.type_key && nav.type_key !== selection.type_key) return false;
  return true;
}

export function searchTree(tree, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return tree;
  const hit = (text) => (text || "").toLowerCase().includes(q);
  return tree
    .map((make) => {
      if (hit(make.label)) return make;
      const models = make.models
        .map((model) => {
          if (hit(model.label)) return model;
          const types = model.types.filter((type) => hit(type.label));
          return types.length ? { ...model, types } : null;
        })
        .filter(Boolean);
      return models.length ? { ...make, models } : null;
    })
    .filter(Boolean);
}

export const selectionLabel = (selection, tree) => {
  if (!selection?.make_key) return null;
  const make = tree.find((m) => m.key === selection.make_key);
  const model = make?.models.find((m) => m.key === selection.model_key);
  const type = model?.types.find((t) => t.key === selection.type_key);
  return [make?.label, model?.label, type?.label].filter(Boolean).join(" › ");
};
